import { TABLE_GRID_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { tableV2FooterControlHeightPx } from '@/features/shared/components/tableSystem/TableV2Primitives';
import { isLightTheme } from '@/theme/semanticTokens';
import { s, spacing } from '@/theme/tokens';
import type { ReactNode } from 'react';

export const DAILY_STATUS_BLOCK_SIZE = 18;
export const DAILY_STATUS_ROW_HEIGHT = 32;
export const HISTORY_PAGE_SIZE_OPTIONS = [5, 10, 25, 50] as const;
export const HISTORY_DEFAULT_PAGE_SIZE = 5;
export const HISTORY_DEFAULT_SORT_DIRECTION = 'asc' as const;
export const HISTORY_DAY_HEADER_MIN_LABEL_WIDTH_PX = 52;
export const HISTORY_STATUS_GRID_GAP_PX = spacing.md;
export const HISTORY_STATUS_CELL_HORIZONTAL_PADDING_PX = spacing.md * 2;
export const HISTORY_NAME_COLUMN_COMPRESSED_MIN_WIDTH_PX = 160;
export const HISTORY_NAME_COLUMN_MIN_WIDTH_PX = 240;
export const HISTORY_NAME_COLUMN_DEFAULT_WIDTH_PX = 420;
export const HISTORY_NAME_COLUMN_MAX_WIDTH_RATIO = 0.5;
export const HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX = 10;
export const HISTORY_FOOTER_CONTROL_HEIGHT_PX = tableV2FooterControlHeightPx;
export const HISTORY_FOOTER_LABEL_FONT_SIZE_PX = 13;
export const HISTORY_TOOLBAR_SKELETON = {
  width: 260,
  height: 14,
  radius: '999px',
} as const;
export const HISTORY_STATE_GRACE_PERIOD_MS = 200;
export const HISTORY_PANEL_CONTENT_HEADER_TOP_PADDING = s('sm');
export const HISTORY_PANEL_CONTENT_HEADER_HEIGHT = TABLE_GRID_HEADER_HEIGHT + 8;

export const resolveHistoryBodyMinHeight = ({
  rowCount,
  rowHeight,
  headerHeight = HISTORY_PANEL_CONTENT_HEADER_HEIGHT,
}: {
  rowCount: number;
  rowHeight: number;
  headerHeight?: number;
}): string => `${headerHeight + Math.max(1, rowCount) * rowHeight}px`;

export const HISTORY_TOOLBAR_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      fontSize: 'subtitle1.fontSize',
      lineHeight: 1.4,
    },
  },
} as const;

export const HEADER_SEPARATOR_SX = {
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 0,
    height: `calc(${TABLE_GRID_HEADER_HEIGHT}px - ${s('md')})`,
    borderRight: '1px solid',
    borderColor: 'divider',
    opacity: 0.72,
  },
} as const;

export const HEADER_TEXT_SX = {
  fontSize: (theme: { typography: { pxToRem: (value: number) => string } }) =>
    theme.typography.pxToRem(13),
  fontWeight: 500,
  lineHeight: 1.4,
} as const;

export const HEADER_SUBTEXT_SX = {
  fontSize: (theme: { typography: { pxToRem: (value: number) => string } }) =>
    theme.typography.pxToRem(11),
  fontWeight: 400,
  lineHeight: 1.2,
  color: (theme: Parameters<typeof isLightTheme>[0]) =>
    isLightTheme(theme) ? 'rgba(0, 0, 0, 0.68)' : 'rgba(244, 247, 251, 0.62)',
} as const;

export const ROW_NAME_TEXT_SX = {
  fontSize: (theme: { typography: { pxToRem: (value: number) => string } }) =>
    theme.typography.pxToRem(13),
  fontWeight: 500,
  lineHeight: 1.4,
  opacity: 0.92,
} as const;

export const NESTED_ROW_NAME_TEXT_SX = {
  fontSize: (theme: { typography: { pxToRem: (value: number) => string } }) =>
    theme.typography.pxToRem(13),
  fontWeight: 400,
  lineHeight: 1.4,
} as const;

export const resolveHistoryDayHeaderFallbackInterval = (dayCount: number): number => {
  if (dayCount >= 26) return 2;
  return 1;
};

export const shouldShowDayHeaderLabel = (
  index: number,
  lastIndex: number,
  interval: number,
): boolean => {
  if (index === 0) return true;
  if (interval <= 1) return index <= lastIndex;
  return index % interval === 0;
};

export const buildDayHeaderLabel = (
  dayLabel: ReactNode,
  index: number,
  lastIndex: number,
  interval: number,
): ReactNode => (shouldShowDayHeaderLabel(index, lastIndex, interval) ? dayLabel : '\u00A0');

export const formatHistoryHeaderDay = (value: Date): string =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
  }).format(value);
